import { DESKTOP_BREAKPOINT } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import { Outlet } from "react-router-dom";

import { BottomNav } from "~/components/shell/BottomNav";
import { SideNav } from "~/components/shell/SideNav";

/**
 * The signed-in frame.
 *
 * Two arrangements of the same tree, switched entirely in CSS:
 *
 * - **Phone** — content, plus a fixed tab bar. The shell has no top bar of its own; the
 *   lockup only really belongs on the dashboard, which renders its own docked header
 *   with the lockup and the calendar button. Every other route carries an in-page back
 *   control instead.
 * - **Desktop** — permanent side rail on the left, content beside it.
 *
 * The content column is a solid surface at every width. The Science Silver gradient is
 * still painted on `<body>`, so it shows through as the rail's backdrop on desktop and
 * behind the sign-in screen — brand in the chrome, calm white where people read.
 *
 * `BottomNav` always mounts and hides itself with a breakpoint, which keeps the switch
 * free of `useMediaQuery` and therefore free of first-paint flicker.
 */
export function AppShell() {
  return (
    <>
      <GlobalStyles
        styles={{
          // Hide chrome, not overflow. Touch, keyboard and assistive scrolling keep
          // using the document; desktop retains the visible six-pixel scrollbar.
          [`@media (max-width:${DESKTOP_BREAKPOINT - 0.05}px)`]: {
            "html, body": { scrollbarWidth: "none" },
            "html::-webkit-scrollbar, body::-webkit-scrollbar": {
              display: "none",
            },
          },
        }}
      />

      <Box sx={{ minHeight: "100dvh", display: "flex" }}>
        <SideNav />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.paper",
          }}
        >
          <Box component="main" sx={{ flex: 1 }}>
            <Outlet />
          </Box>
          <BottomNav />
        </Box>
      </Box>
    </>
  );
}
